import {observer} from 'mobx-react';
import React from 'react';
import {pagedListResponseUtils, User} from '../../../../domain';
import {TooltipDiv, TooltipSpan} from '../../../../lib/components';
import {coalesce, concat_ws, dateFormat} from '../../../../lib/helpers';
import {routes} from '../../../../pages/Routes';
import {Classroom, ClassroomStatusLabels} from '../../../classroom/models/classroom-models';
import {AdminClassroomSceneStore} from '../AdminClassroomSceneStore';

const utils = pagedListResponseUtils;

function userTooltipContent(user: User): React.ReactNode {
  return (
    <div>
      <div>Username: {user.Username}</div>
      <div>First Name: {user.FirstName}</div>
      <div>Last Name: {user.LastName}</div>
      <div>Email: {user.Email}</div>
    </div>);
}

function pagingControls(store: AdminClassroomSceneStore): React.ReactNode {
  const search = store.classroomSearch;
  const page = search.resultState?.value;

  if (page === null || page === undefined)
    return null;

  return (
    <div className="row justify-content-center align-items-center" key="page-controls">
      <div className="col-auto">
        <button type="button" className="btn btn-sm btn-primary"
                disabled={!search.hasPreviousPage}
                onClick={search.loadPreviousPage}>
          <span className="fas fa-angle-double-left"/>
        </button>
      </div>
      <div className="col-auto">
        Total: {page.CountRecords}.
        Showing from {utils.startIndex(page)} to {utils.endIndex(page)}.
      </div>
      <div className="col-auto">
        <button type="button" className="btn btn-sm btn-primary"
                disabled={!search.hasNextPage}
                onClick={search.loadNextPage}>
          <span className="fas fa-angle-double-right"/>
        </button>
      </div>
    </div>);
}

function renderTable(store: AdminClassroomSceneStore): React.ReactNode {
  const search = store.classroomSearch;
  const resultState = search.resultState;

  if (resultState?.isResolved !== true)
    return null;

  const page = resultState.value;
  if (page.Data === null || page.Data.length === 0)
    return null;

  const deleteClassroomId = coalesce(store.deleteClassroom?.classroom?.Id, -1);
  const updateClassroomId = coalesce(store.deleteClassroom?.classroom?.Id, -1);

  function highlightRowClassname(classroomId: number): string {
    if (classroomId === deleteClassroomId)
      return 'tr-danger';
    if (classroomId === updateClassroomId)
      return 'tr-info';
    return '';
  }

  const startIndex = utils.startIndex(page);
  const pickable = store.mode === 'add-classroom' || store.mode === 'edit-classroom';
  const editFormTeacherId = coalesce(store.currentEditForm?.teacher.value?.Id, -1);
  const editFormStudentId = coalesce(store.currentEditForm?.student.value?.Id, -1);
  const editFormLessonId = coalesce(store.currentEditForm?.lesson.value?.Id, -1);
  const editFormDateTime = store.editFormDateTime();
  let selectedStudentId = coalesce(store.studentSearch.selectUser?.Id, -1);
  let selectedTeacherId = coalesce(store.teacherSearch.selectUser?.Id, -1);

  const canBeginDelete = store.mode === 'view';
  const canBeginEdit = store.mode === 'view';

  function renderTableRow(classroom: Classroom, index: number): React.ReactNode {
    const student = classroom.Student!;
    const teacher = classroom.Teacher!;
    const lesson = classroom.Lesson!;

    const scheduledStart = classroom.ScheduledStartTime;
    const startDate = dateFormat(scheduledStart, 'ymd', true);
    const startTime = dateFormat(scheduledStart, 'hm', true);

    const actualStartTime = dateFormat(classroom.ActualStartTime, 'hms', true);
    const actualEndTime = dateFormat(classroom.ActualEndTime, 'hms', true);

    return (
      <tr key={classroom.Id}
          className={highlightRowClassname(classroom.Id)}>
        <td>{startIndex + index}</td>

        {/* student */}
        <td>
          <div className="row no-gutters">
            <div className="col">
              <TooltipSpan tooltipText={userTooltipContent(student)}>
                {concat_ws([student.FirstName, student.LastName])}
              </TooltipSpan>
            </div>
            <div className="col-auto">
              {
                student.Id !== selectedStudentId &&
                <button type="button" className="btn btn-sm btn-outline-primary"
                        onClick={() => store.studentSearch.setSelectedUser(student)}>
                  <i className="fas fa-filter"/>
                </button>
              }
              {
                pickable &&
                <button type="button" className="btn btn-sm btn-outline-primary"
                        disabled={student.Id === editFormStudentId}
                        onClick={() => store.pickStudent(student)}>
                  <i className="fas fa-check"/>
                </button>
              }
            </div>
          </div>
        </td>

        {/* teacher */}
        <td>
          <div className="row no-gutters">
            <div className="col">
              <TooltipDiv tooltipText={userTooltipContent(teacher)}>
                {concat_ws([teacher.FirstName, teacher.LastName])}
              </TooltipDiv>
            </div>
            <div className="col-auto">
              {
                teacher.Id !== selectedTeacherId &&
                <button type="button" className="btn btn-sm btn-outline-primary"
                        onClick={() => store.teacherSearch.setSelectedUser(teacher)}>
                  <i className="fas fa-filter"/>
                </button>
              }
              {
                pickable &&
                <button type="button" className="btn btn-sm btn-outline-primary"
                        disabled={teacher.Id === editFormTeacherId}
                        onClick={() => store.pickTeacher(teacher)}>
                  <i className="fas fa-check"/>
                </button>
              }
            </div>
          </div>
        </td>

        {/* lesson */}
        <td>
          <div className="row no-gutters">
            <div className="col">
              {concat_ws([lesson.Unit?.Name, lesson.Name], '/')}
            </div>
            <div className="col-auto">
              {
                pickable &&
                <button type="button" className="btn btn-sm btn-outline-primary"
                        onClick={() => store.pickLessonFromClassroom(classroom)}
                        disabled={editFormLessonId === lesson.Id}>
                  <i className="fas fa-check"/>
                </button>
              }
            </div>
          </div>
        </td>

        {/* date */}
        <td>
          <div className="row no-gutters">
            <div className="col">
              <div>Scheduled: {startDate + ' ' + startTime}</div>
              {
                actualStartTime !== null &&
                <div>Actual: {actualStartTime + '-' + actualEndTime}</div>
              }
            </div>
            <div className="col-auto">
              {
                search.startDateFilter.value !== startDate &&
                <button type="button" className="btn btn-sm btn-outline-primary"
                        onClick={() => store.classroomSearch.startDateFilter.setValue(startDate)}>
                  <i className="fas fa-filter"/>
                </button>
              }
              {
                pickable &&
                <button type="button" className="btn btn-sm btn-outline-primary"
                        disabled={editFormDateTime === scheduledStart}
                        onClick={() => store.pickDateTimeFromClassroom(classroom)}>
                  <i className="fas fa-check"/>
                </button>
              }
            </div>
          </div>
        </td>

        <td>{ClassroomStatusLabels[classroom.Status]}</td>

        <td className="no-wrap">
          <a href={routes.classroomDetails(classroom.Id)}
             target="_blank"
             rel="noreferrer noopener"
             className="btn btn-sm btn-primary" role="button">
            <span className="fas fa-search"/>
          </a>
          <button type="button" className="btn btn-sm btn-primary"
                  onClick={() => store.beginEdit(classroom)}
                  disabled={!canBeginEdit}>
            <span className="fas fa-pen"/>
          </button>
          <button type="button" className="btn btn-sm btn-danger"
                  onClick={() => store.beginDelete(classroom)}
                  disabled={!canBeginDelete}>
            <span className="fas fa-trash"/>
          </button>
        </td>
      </tr>
    );
  }

  return (
    <div className="card-body">
      <table className="table table-sm table-striped table-bordered">
        <thead>
        <tr>
          <th className="min-width">
            {/* # */}
          </th>

          {/* student */}
          <th>Student</th>

          {/* teacher */}
          <th>Teacher</th>

          {/* lesson */}
          <th>Lesson</th>

          {/* date-time */}
          <th>Date</th>

          <th>Status</th>

          <th className="min-width">
            {/* actions */}
          </th>
        </tr>
        </thead>
        <tbody>
        {
          page.Data.map((classroom, index) =>
            renderTableRow(classroom, index))
        }
        </tbody>
      </table>
    </div>
  );
}

@observer
export class ClassroomSearchResults extends React.Component<{ store: AdminClassroomSceneStore }> {
  render() {
    const {store} = this.props;

    if (store.classroomSearch.resultState?.isResolved !== true)
      return null;

    if (!store.classroomSearch.expanded)
      return null;

    return (
      <div className="card classroom-search-results" key="results">
        <div className="card-header">
          {pagingControls(store)}
        </div>
        {renderTable(store)}
      </div>
    );
  }
}
